import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import { useChatStore } from '@stores/chat.store';
import { useAdvertiserStore } from '@stores/advertiser';
import { useAnalytics } from '@hooks/analytics';
import { useBlockUser } from '@hooks/api/chat-websocket';
import { CHAT_CONVERSATION_TYPES, USER_TAG } from '@/utils/constants';

import { BLOCK_EVENTS } from './utils/block-section.constants';

/** Gerencia estado de bloqueio/desbloqueio de usuário: modal, loading, analytics. */
export const useBlockState = () => {
	const conversationActive = useChatStore((s) => s.conversation.active);
	const conversationChatType = useChatStore((s) => s.conversation.chatType);
	const messageListSource = useChatStore((s) => s.messageList.source);
	const advertiserId = useAdvertiserStore((s) => s.source?.Retorno?.Id);
	const { updateBlockUser } = useBlockUser();
	const { trackEvent } = useAnalytics();

	const isBlocked = conversationActive?.userTag === USER_TAG.Blocked;
	const chatId = conversationActive?.chatId;
	const userId = `PF:${advertiserId}`;

	const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
	const [isBlockLoading, setIsBlockLoading] = useState(false);
	const blockRequestedRef = useRef(false);
	const isBlockedAtRequestRef = useRef<boolean | null>(null);

	useEffect(() => {
		if (
			blockRequestedRef.current &&
			isBlockedAtRequestRef.current !== null &&
			isBlocked !== isBlockedAtRequestRef.current
		) {
			blockRequestedRef.current = false;
			isBlockedAtRequestRef.current = null;
			setIsBlockLoading(false);
			setIsBlockModalOpen(false);
		}
	}, [isBlocked]);

	const userIdToBlock = useMemo(() => {
		const chatTypePurchase =
			conversationChatType === CHAT_CONVERSATION_TYPES.purchase;

		const fromConversation = chatTypePurchase
			? conversationActive?.userIdTo
			: conversationActive?.userIdFrom;

		const lastMessage =
			messageListSource?.[messageListSource.length - 1];
		const fromMessagesUserId =
			lastMessage?.chat?.[
				`userId${chatTypePurchase ? CHAT_CONVERSATION_TYPES.Sale : CHAT_CONVERSATION_TYPES.Purchase}`
			];

		return fromConversation || fromMessagesUserId;
	}, [
		conversationChatType,
		conversationActive,
		messageListSource,
	]);

	const handleConfirmBlock = useCallback(() => {
		if (!chatId || !userId || !userIdToBlock) return;

		if (isBlocked) {
			trackEvent('userInteraction', BLOCK_EVENTS.confirmUnblock);
		} else {
			trackEvent('userInteraction', BLOCK_EVENTS.confirmBlock);
		}

		blockRequestedRef.current = true;
		isBlockedAtRequestRef.current = isBlocked;
		setIsBlockLoading(true);

		updateBlockUser({
			chatId,
			userId,
			userIdToBlock,
			blocked: !isBlocked,
		});
	}, [chatId, userId, userIdToBlock, isBlocked, updateBlockUser, trackEvent]);

	const handleBlockMenuClick = useCallback(() => {
		if (isBlocked) {
			trackEvent('userInteraction', BLOCK_EVENTS.unblockMenuClick);
		} else {
			trackEvent('userInteraction', BLOCK_EVENTS.blockMenuClick);
		}
		setIsBlockModalOpen(true);
	}, [isBlocked, trackEvent]);

	const handleBlockModalOpenChange = useCallback(
		(open: boolean) => {
			if (!open && isBlockModalOpen) {
				if (isBlocked) {
					trackEvent('userInteraction', BLOCK_EVENTS.cancelUnblock);
				} else {
					trackEvent('userInteraction', BLOCK_EVENTS.cancelBlock);
				}
			}
			setIsBlockModalOpen(open);
		},
		[isBlockModalOpen, isBlocked, trackEvent],
	);

	const handleCancelBlock = useCallback(() => {
		if (isBlocked) {
			trackEvent('userInteraction', BLOCK_EVENTS.cancelUnblock);
		} else {
			trackEvent('userInteraction', BLOCK_EVENTS.cancelBlock);
		}
		setIsBlockModalOpen(false);
	}, [isBlocked, trackEvent]);

	const trackChatMenuOpen = useCallback(() => {
		trackEvent('userInteraction', BLOCK_EVENTS.menuOpen);
	}, [trackEvent]);

	return {
		isBlocked,
		isBlockModalOpen,
		isBlockLoading,
		handleConfirmBlock,
		handleBlockMenuClick,
		handleBlockModalOpenChange,
		handleCancelBlock,
		trackChatMenuOpen,
	};
};
